import React, {useState} from "react"
import {Input, TextArea, ValidationButton} from "@irati/ui/src/FormItem"
import FormattedMessage, {useIntl} from "@irati/utils/lang"
import BudgetSelect from "@irati-web/recognition/src/components/Popup/RewardRecognition/BudgetSelect"
import {Budget, User} from "@irati/types"
import UserSearch from "@irati/ui/src/UserSearch"
import Validation from "@irati/utils/validation"
import {Body} from "@irati/ui/src/Typography"
import {formatCurrency} from "@irati/utils/currency/formatCurrency"
import {useTheme} from "@irati/ui/src/global/useTheme"
import {useTenantCurrencyExchangeQuery} from "@irati/currency/hooks"
import Form from "@irati/ui/src/Form"
import {PointsRequest} from "."
import Spacing from "@irati/ui/src/Spacing"
import styled from "styled-components"

interface Props {
  onDone: (request: PointsRequest) => void
}

const StyledForm = styled(Form)`
  textarea {
    resize: vertical;
    min-height: 40px;
  }
`
const RequestPointsForm = (props: Props) => {
  const {onDone} = props
  const theme = useTheme()
  const {tenantCurrency, currencyExchange} = useTenantCurrencyExchangeQuery()
  const [budget, setBudget] = useState<Budget>()
  const [reason, setReason] = useState("")
  const [pointsRequest, setPointsRequest] = useState(0)
  const [approver, setApprover] = useState<User>()
  const intl = useIntl()
  const onBudgetChange = (newBudget: Budget) => {
    setBudget(newBudget)
  }
  const sendRequest = () => {
    const requestToSend = {
      budget,
      reason,
      pointsRequest,
      approver
    }
    onDone(requestToSend as never as PointsRequest)
  }
  const handlePointsPerUser = (newPointsRequest: string) => {
    setPointsRequest(parseInt(newPointsRequest, 10))
  }
  const handleApproverChange = (_: any, selectedUser: User) => {
    setApprover(selectedUser)
  }
  const convertedRp = pointsRequest && currencyExchange?.rpExchangeRate ? (pointsRequest / currencyExchange?.rpExchangeRate).toFixed(2) : 0

  return (
    <StyledForm>
      <BudgetSelect
        validation={Validation.string("platform.defaultValidation.required")}
        onBudgetChange={onBudgetChange}
        label={intl.formatMessage({id: "wallet.reward.requestPoints.label.budget"})}
      />
      <Spacing marginTop={3}>
        <UserSearch
          id="request-approver-selector"
          validation={Validation.string("platform.defaultValidation.required")}
          label="wallet.reward.requestPoints.label.approver"
          onChange={handleApproverChange}
          placeholder="wallet.reward.requestPoints.placeholder.approver"
        />
      </Spacing>
      <Input
        name="pointsRequested"
        type="number"
        min="1"
        flush
        label="wallet.reward.requestPoints.label.requestAmount"
        value={String(pointsRequest)}
        placeholder="wallet.reward.requestPoints.placeholder.requestAmount"
        validation={Validation.string(intl.formatMessage({id: "platform.validation.number.nonZeroValue"})).test(
          "Positive, value above 1 and no decimal",
          intl.formatMessage({id: "platform.validation.number.minOne"}),
          (val) => val >= 1
        )}
        onChange={handlePointsPerUser}
        addonBackground={theme.colors.greys.grey03}
        addon={{
          after: (
            <Body small>
              <FormattedMessage
                id="wallet.reward.distribute.totalConverted.lbl"
                values={{
                  total: convertedRp ? intl.formatNumber(convertedRp) : 0,
                  currency: formatCurrency({currencyCode: tenantCurrency})
                }}
              />
            </Body>
          )
        }}
      />
      <Spacing marginTop={3}>
        <TextArea
          label="wallet.reward.requestPoints.label.reason"
          placeholder="wallet.reward.requestPoints.placeholder.reason"
          name="reasonBox"
          onChange={setReason}
          value={reason}
          validation={Validation.string("platform.defaultValidation.required")}
          rows={5}
        />
      </Spacing>
      <ValidationButton full primary marginTop={3} onClick={sendRequest}>
        <FormattedMessage id="wallet.reward.requestPoints.btn.requestPoints" />
      </ValidationButton>
    </StyledForm>
  )
}
export default RequestPointsForm
